import React, { useEffect, useState, useRef } from 'react';
import { QrReader } from 'react-qr-reader';
import {
  CameraView,
  useCameraPermissions,
  BarcodeScanningResult,
} from 'expo-camera';

import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { notificationService } from '../services/notification-service';
import { AppDispatch, RootState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import { chargerQrScan } from '../store/actions/charging-actions';
import { ChargerAttributes } from '../models/charger-model';

const { width, height } = Dimensions.get('window');

const extractSerialNumberFromQr = (qrCodeText: string): string | null => {
  // Use MEANDER_ENV to determine the correct domain
  const meanderEnv = process.env.MEANDER_ENV || 'production';
  const baseDomain = meanderEnv === 'production' ? 'app' : 'stage';

  // Regex that matches both http and https protocols
  const regex = new RegExp(
    `^https?://${baseDomain}\\.meanderev\\.co/chargerscan/(.+)$`
  );

  const match = qrCodeText.match(regex);

  // Returns the serialNumber or null if no match
  return match ? match[1] : null;
};

interface QRScannerProps {
  navigation: any;
  myLocation: any;
}

const QRScanner = ({ navigation, myLocation }: QRScannerProps) => {
  const dispatch: AppDispatch = useDispatch();
  const [hasPermission, setHasPermission] = useState<null | Boolean>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const selectedCharger: ChargerAttributes = useSelector(
    (state: RootState) => state.chargers.selectedCharger
  ) as ChargerAttributes;
  const [_status, requestPermission] = useCameraPermissions();
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const cameraRef = useRef<any>(null);

  React.useEffect(() => {
    const getBarCodeScannerPermissions = async () => {
      try {
        const { status } = await requestPermission();
        setHasPermission(status === 'granted');
      } catch (error) {
        console.error('Error requesting camera permission:', error);
        setHasPermission(false);
      }
    };

    getBarCodeScannerPermissions();
  }, []);

  // Function to handle QR code scan result
  const handleScan = (result: any, error: any, codeReader: any) => {
    if (result && !isProcessing) {
      setIsProcessing(true);
      const serialNumber = extractSerialNumberFromQr(result.getText());
      if (serialNumber === null) {
        notificationService.sendNotification(
          'error',
          'Not a valid MeanderEV QR Code!'
        );
        setIsProcessing(false);
      } else {
        //get the charger data and select the charger
        dispatch(chargerQrScan(serialNumber));
        setShouldNavigate(true);
      }
    }
  };

  /**
   * Handle the result of the native QR code scanner
   * @param result The result of the QR code scan
   */
  const handleScanNative = (result: BarcodeScanningResult) => {
    if (result && !isProcessing) {
      setIsProcessing(true);
      const serialNumber = extractSerialNumberFromQr(result.data);
      if (serialNumber === null) {
        notificationService.sendNotification(
          'error',
          'Not a valid MeanderEV QR Code!'
        );
        setIsProcessing(false);
      } else {
        // Get the charger data and select the charger
        dispatch(chargerQrScan(serialNumber));
        setShouldNavigate(true);
      }
    }
  };

  useEffect(() => {
    if (selectedCharger && selectedCharger.uuid !== '' && shouldNavigate) {
      navigation.navigate('ChargerStation', { searchCharger: true });
    }

    return () => {
      setShouldNavigate(false);
      setIsProcessing(false);
      setIsActive(false);
    };
  }, [selectedCharger, shouldNavigate]);

  // Cleanup effect to ensure camera is stopped when component unmounts
  useEffect(() => {
    return () => {
      setIsActive(false);
      if (cameraRef.current) {
        // Try to stop the camera if possible
        try {
          cameraRef.current?.stopRecording?.();
        } catch (error) {
          // Silent cleanup error - camera may already be stopped
        }
      }
    };
  }, []);

  // Show loading state while permission is being requested
  if (hasPermission === null) {
    return (
      <SafeAreaView>
        <View
          style={{
            height: height - 65,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text className="text-lg font-bold text-center text-white">
            Requesting camera permission...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Show error state if permission is denied
  if (hasPermission === false) {
    return (
      <SafeAreaView>
        <View
          style={{
            height: height - 65,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text className="text-lg font-bold text-center text-white">
            Camera permission is required to scan QR codes
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView>
      <View style={{ height: height - 65 }}>
        <View style={{ flex: 1 }}>
          <Text className="text-lg font-bold text-center text-white">
            Scan QR Code
          </Text>
          <Text
            className="text-lg font-normal text-center"
            style={{ color: '#8D8E90' }}
          >
            Please point the camera at the QR Code
          </Text>
          {Platform.OS === 'web' ? (
            <QrReader
              constraints={{ advanced: [{ facingMode: 'environment' }] }} // Specify media track constraints
              onResult={handleScan} // Callback function for QR code scan result
              //error={handleError} // Callback function for scan error
              scanDelay={300} // Delay between scans in milliseconds
              videoId="qr-video" // ID of the video element
              className="qr-scanner" // Optional class name for styling
              containerStyle={{ width: '100%' }} // Style for the container
              videoContainerStyle={{ width: '100%' }} // Style for the video container
              videoStyle={{ width: '100%' }} // Style for the video
            />
          ) : (
            isActive && (
              <CameraView
                ref={cameraRef}
                onBarcodeScanned={handleScanNative}
                barcodeScannerSettings={{
                  barcodeTypes: ['qr'],
                }}
                style={{ flex: 1 }}
              />
            )
          )}
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({});

export default QRScanner;
